import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  Paper,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  CardActionArea,
  Tooltip,
  Avatar,
  Zoom,
  alpha,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Fade
} from '@mui/material';
import { getTenantApiUrl } from '../utils/api';
import {
  Receipt as ReceiptIcon,
  ShoppingCart as ShoppingCartIcon,
  Assignment as AssignmentIcon,
  Undo as UndoIcon,
  AccountBalance as AccountBalanceIcon,
  Payment as PaymentIcon,
  Store as StoreIcon,
  Menu as MenuIcon,
  Assessment as ReportsIcon,
  AccountCircle as AccountIcon,
  ExitToApp as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthProvider';

interface MenuItemType {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  route: string;
  color: string;
  gradientFrom: string;
  gradientTo: string;
}

const menuItems: MenuItemType[] = [
  {
    id: 'sales-invoice',
    title: 'فاتورة مبيعات',
    subtitle: 'إنشاء فاتورة مبيعات جديدة',
    icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
    route: '/invoice/create',
    color: '#1976d2',
    gradientFrom: '#1976d2',
    gradientTo: '#42a5f5',
  },
  {
    id: 'sales-discount',
    title: 'إشعار خصم مبيعات',
    subtitle: 'إنشاء إشعار خصم للمبيعات',
    icon: <AssignmentIcon sx={{ fontSize: 40 }} />,
    route: '/sales-discount/create',
    color: '#f57c00',
    gradientFrom: '#f57c00',
    gradientTo: '#ffb74d',
  },
  {
    id: 'purchase-invoice',
    title: 'فاتورة مشتريات',
    subtitle: 'إنشاء فاتورة مشتريات جديدة',
    icon: <ShoppingCartIcon sx={{ fontSize: 40 }} />,
    route: '/purchase/create',
    color: '#388e3c',
    gradientFrom: '#388e3c',
    gradientTo: '#66bb6a',
  },
  {
    id: 'purchase-return',
    title: 'إرجاع مشتريات',
    subtitle: 'إنشاء إرجاع للمشتريات',
    icon: <UndoIcon sx={{ fontSize: 40 }} />,
    route: '/purchase-return/create',
    color: '#d32f2f',
    gradientFrom: '#d32f2f',
    gradientTo: '#f44336',
  },
  {
    id: 'receipt-voucher',
    title: 'سند قبض',
    subtitle: 'إنشاء سند قبض جديد',
    icon: <AccountBalanceIcon sx={{ fontSize: 40 }} />,
    route: '/receipt/create',
    color: '#7b1fa2',
    gradientFrom: '#7b1fa2',
    gradientTo: '#ba68c8',
  },
  {
    id: 'payment-voucher',
    title: 'سند صرف',
    subtitle: 'إنشاء سند صرف جديد',
    icon: <PaymentIcon sx={{ fontSize: 40 }} />,
    route: '/payment/create',
    color: '#c2185b',
    gradientFrom: '#c2185b',
    gradientTo: '#e91e63',
  },
];

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [selectedBranch, setSelectedBranch] = useState('');
  const [showCards, setShowCards] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [branches, setBranches] = useState<{id: number, name: string}[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(true);

  const fetchBranches = async () => {
    try {
      setBranchesLoading(true);
      // Get tenant name from localStorage for API URL
      const tenantName = localStorage.getItem('tenant_name');
      
      let response: Response;
      let data: any;
      
      if (tenantName) {
        // Try tenant subdomain first
        const tenantUrl = `${getTenantApiUrl(tenantName)}/v1/default/mybranches`;
        try {
          response = await fetch(tenantUrl, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            data = await response.json();
          } else {
            throw new Error('Tenant subdomain failed');
          }
        } catch (error) {
          // Fallback to main domain with tenant parameter
          console.warn('Tenant subdomain failed, falling back to main domain:', error);
          const fallbackUrl = `/api/v1/default/mybranches?tenant=${tenantName}`;
          response = await fetch(fallbackUrl, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
          data = await response.json();
        }
      } else {
        // No tenant, use default endpoint
        response = await fetch('/api/v1/default/mybranches', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        data = await response.json();
      }
      
      if (data && Array.isArray(data)) {
        // Map API response to match our interface
        const mappedBranches = data.map((branch: any) => ({
          id: branch.id,
          name: branch.name_a_original || branch.name_a || branch.name || `فرع ${branch.code}`
        }));
        setBranches(mappedBranches);
        
        // Set default branch if available  
        if (mappedBranches.length > 0) {
          setSelectedBranch(mappedBranches[0].id.toString());
        }
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      // Fallback to a basic branch if API fails
      setBranches([{ id: 1, name: 'الفرع الرئيسي' }]);
      setSelectedBranch('1');
    } finally {
      setBranchesLoading(false);
    }
  };

  useEffect(() => {
    // Fetch branches from API
    fetchBranches();
    
    // Trigger animation after component mounts
    const timer = setTimeout(() => {
      setShowCards(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  const handleMenuItemClick = (item: MenuItemType) => {
    if (!selectedBranch) {
      alert('يرجى اختيار الفرع أولاً');
      return;
    }
    
    // Store selected branch in localStorage or context
    localStorage.setItem('selectedBranch', selectedBranch);
    navigate(item.route);
  };

  const toggleDrawer = (_event?: React.SyntheticEvent) => {
    setDrawerOpen((prev) => !prev);
  };

  const handleDrawerClose = (
    _event: React.SyntheticEvent | {},
    _reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    setDrawerOpen(false);
  };

  const menuItems_sidebar = [
    {
      id: 'sales-invoice',
      title: 'فاتورة مبيعات',
      icon: <ReceiptIcon />,
      route: '/invoice/create',
    },
    {
      id: 'purchase-invoice',
      title: 'فاتورة مشتريات',
      icon: <ShoppingCartIcon />,
      route: '/purchase/create',
    },
    {
      id: 'receipt-voucher',
      title: 'سند قبض',
      icon: <AccountBalanceIcon />,
      route: '/receipt/create',
    },
    {
      id: 'payment-voucher',
      title: 'سند صرف',
      icon: <PaymentIcon />,
      route: '/payment/create',
    },
    {
      id: 'sales-discount',
      title: 'إشعار خصم مبيعات',
      icon: <AssignmentIcon />,
      route: '/sales-discount/create',
    },
    {
      id: 'purchase-return',
      title: 'إرجاع مشتريات',
      icon: <UndoIcon />,
      route: '/purchase-return/create',
    },
    {
      id: 'reports',
      title: 'التقارير',
      icon: <ReportsIcon />,
      route: '/reports',
    },
  ];

  const handleSidebarItemClick = (route: string) => {
    navigate(route);
    setDrawerOpen(false);
  };

  return (
    <Box>
      {/* Top Navigation Bar */}
      <AppBar 
        position="sticky" 
        sx={{ 
          background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          {/* Menu Items - Will appear on RIGHT in RTL */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            
            <Tooltip title={`${user?.name} (${user?.user_code})`}>
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  cursor: 'pointer',
                }}
              >
                <AccountIcon />
              </Avatar>
            </Tooltip>
            
            <Tooltip title="تسجيل الخروج">
              <IconButton color="inherit" onClick={logout}>
                <LogoutIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="القائمة">
              <IconButton
                color="inherit"
                edge="end"
                aria-label="menu"
                onClick={toggleDrawer}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Logo and Title - Will appear on LEFT in RTL */}
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold', textAlign: 'right', mr: 2 }}>
            نظام خبير
          </Typography>
          
          <StoreIcon sx={{ fontSize: 32 }} />
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerClose}
        ModalProps={{
          keepMounted: false,
        }}
        SlideProps={{
          direction: 'left'
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            background: 'linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)',
            direction: 'rtl', // Ensure RTL content inside drawer
          },
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
          <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 1, bgcolor: 'rgba(255,255,255,0.2)' }}>
            <AccountIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {user?.name}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {user?.user_code}
          </Typography>
        </Box>
        
        <List dense>
          {menuItems_sidebar.map((item) => (
            <ListItemButton 
              key={item.id}
              onClick={() => handleSidebarItemClick(item.route)}
              sx={{
                mx: 1,
                my: 0.5,
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'primary.light',
                  color: 'white',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ color: 'primary.main', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.title}
                sx={{ '& .MuiTypography-root': { fontWeight: 500 } }}
              />
            </ListItemButton>
          ))}
        </List>
        
        {/* Spacer to push logout to bottom */}
        <Box sx={{ flexGrow: 1 }} />
        
        <Divider sx={{ my: 1 }} />
        
        <ListItemButton 
          onClick={logout}
          sx={{
            mx: 1,
            my: 0.5,
            borderRadius: 2,
            color: 'error.main',
            maxHeight: 40,
            '&:hover': {
              bgcolor: 'error.light',
              color: 'white',
              '& .MuiListItemIcon-root': {
                color: 'white',
              },
            },
          }}
        >
          <ListItemIcon sx={{ color: 'error.main', minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="تسجيل الخروج" 
            sx={{ '& .MuiTypography-root': { fontWeight: 500 } }}
          />
        </ListItemButton>
      </Drawer>

      <Box
        sx={{
          minHeight: 'calc(100vh - 64px)',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          py: 4,
        }}
      >
        <Container maxWidth="lg">
          {/* Welcome Section */}
          <Fade in timeout={1000}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                mb: 4,
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Box display="flex" alignItems="center" mb={3}>
                <StoreIcon sx={{ fontSize: 48, color: '#1976d2', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="h4" sx={{ fontWeight: 'bold', lineHeight: 2, color: '#333' }}>
                    مرحباً بك في نظام خبير
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    {user?.name} - اختر العملية المطلوبة
                  </Typography>
                </Box>
              </Box>

              {/* Branch Selector */}
              <FormControl fullWidth sx={{ maxWidth: 400 }}>
                <InputLabel id="branch-select-label">اختر الفرع</InputLabel>
                <Select
                  labelId="branch-select-label"
                  value={selectedBranch}
                  label="اختر الفرع"
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  disabled={branchesLoading}
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                  }}
                >
                  {branchesLoading ? (
                    <MenuItem disabled>جاري تحميل الفروع...</MenuItem>
                  ) : (
                    branches.map((branch) => (
                      <MenuItem key={branch.id} value={branch.id.toString()}>
                        {branch.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Paper>
          </Fade>

        {/* Menu Items Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 3,
          }}
        >
          {menuItems.map((item, index) => (
            <Zoom
              key={item.id}
              in={showCards}
              timeout={1000}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <Card
                elevation={6}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  overflow: 'hidden',
                  position: 'relative',
                  background: `linear-gradient(135deg, ${item.gradientFrom}, ${item.gradientTo})`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: `0 20px 40px ${alpha(item.color, 0.3)}`,
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(255, 255, 255, 0.1)',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                  },
                  '&:hover::before': {
                    opacity: 1,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleMenuItemClick(item)}
                  sx={{ height: '100%', p: 0 }}
                >
                  <CardContent
                    sx={{
                      height: '200px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        mb: 2,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.1) rotate(5deg)',
                        },
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: 'bold',
                        color: 'white',
                        mb: 1,
                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '0.9rem',
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      }}
                    >
                      {item.subtitle}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Zoom>
          ))}
        </Box>

        {/* Footer */}
        <Fade in timeout={1500}>
          <Box mt={6} textAlign="center">
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              © 2025 نظام خبير - جميع الحقوق محفوظة
            </Typography>
          </Box>
        </Fade>
      </Container>
    </Box>
    </Box>
  );
};

export default HomePage;
